import {
  selectHasRecipes,
  selectInitialSyncPending,
  selectHomeCards,
  selectHomeCardOrder,
  useAppSelector,
  useT,
  type HomeCard,
} from "@reciperoll/core";
import type { ReactNode } from "react";
import QuickAdd from "../components/home/QuickAdd";
import ContinueCooking from "../components/home/ContinueCooking";
import ShoppingListPeek from "../components/home/ShoppingListPeek";
import WhatCanIMake from "../components/home/WhatCanIMake";
import TodaysMealPlan from "../components/home/TodaysMealPlan";
import Collections from "../components/home/Collections";
import CookItAgain from "../components/home/CookItAgain";
import QuickRecipes from "../components/home/QuickRecipes";
import RecentlyAdded from "../components/home/RecentlyAdded";
import RecentlyViewed from "../components/home/RecentlyViewed";
import EmptyHome from "../components/home/EmptyHome";
import LoadingHome from "../components/home/LoadingHome";

// Home mirrors the RN home screen. The greeting + search live in the app header
// (AppLayout), so "search" isn't a card here. Cards honour the synced Customise
// home prefs (visibility + order); editing those lives in the mobile app for now.
export default function Home() {
  const t = useT();
  const hasRecipes = useAppSelector(selectHasRecipes);
  // While the first sync is still pulling data down, show a loader rather than
  // flashing the empty state (see selectInitialSyncPending).
  const initialSyncPending = useAppSelector(selectInitialSyncPending);
  const home = useAppSelector(selectHomeCards);
  const order = useAppSelector(selectHomeCardOrder);

  if (!hasRecipes) {
    return initialSyncPending ? <LoadingHome /> : <EmptyHome />;
  }

  // Search is rendered in the app header on web, so it maps to nothing here.
  const node: Record<HomeCard, ReactNode> = {
    search: null,
    continueCooking: <ContinueCooking />,
    shoppingList: <ShoppingListPeek />,
    whatCanIMake: <WhatCanIMake />,
    mealPlan: <TodaysMealPlan />,
    collections: <Collections />,
    cookItAgain: <CookItAgain />,
    quickRecipes: <QuickRecipes />,
    recentlyAdded: <RecentlyAdded />,
    recentlyViewed: <RecentlyViewed />,
  };

  // Shopping list + What-can-I-make pair side-by-side (grid-cols-2) only when
  // they're adjacent in the order (either way round); otherwise each is full width.
  const PAIRABLE = new Set<HomeCard>(["shoppingList", "whatCanIMake"]);
  const visible = order.filter((c) => home[c]);
  const elements: ReactNode[] = [];
  for (let i = 0; i < visible.length; i++) {
    const card = visible[i];
    const next = visible[i + 1];
    if (next && card !== next && PAIRABLE.has(card) && PAIRABLE.has(next)) {
      elements.push(
        <div key={card} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          {node[card]}
          {node[next]}
        </div>,
      );
      i++;
    } else if (node[card]) {
      elements.push(<div key={card}>{node[card]}</div>);
    }
  }

  return (
    <div className="max-w-5xl mx-auto">
      <QuickAdd />
      {elements.length ? (
        elements
      ) : (
        <div className="text-center py-24 text-ink-soft">{t("home.clearedBody")}</div>
      )}
    </div>
  );
}
